import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  Stack,
  Paper,
  TextField,
  MenuItem,
  InputAdornment,
  keyframes,
  Chip,
  Divider,
} from '@mui/material';
import {
  ArrowDownward,
  Search,
  HomeWork,
  Assignment,
  Build,
  Star,
  DateRange,
  Place,
  Verified,
  Bed,
  MonetizationOn,
} from '@mui/icons-material';

/* ================= Animations ================= */
const textFadeInUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-12px); }
  60% { transform: translateY(-6px); }
`;

const floatY = keyframes`
  0% { transform: translateY(0) }
  50% { transform: translateY(-8px) }
  100% { transform: translateY(0) }
`;

/* ================= Component ================= */
export default function ConstructionHeroFinal() {
  const [animate, setAnimate] = useState(false);
  const heroVideoSrc = 'https://www.pexels.com/download/video/7347879/';

  useEffect(() => {
    const t = setTimeout(() => setAnimate(true), 80);
    return () => clearTimeout(t);
  }, []);

  const createAnimationStyle = (delay) => ({
    opacity: animate ? 1 : 0,
    animation: animate ? `${textFadeInUp} .75s ease-out forwards` : 'none',
    animationDelay: delay,
  });

  // Quick facts (kept from your version, prettified with icons)
  const stats = [
    { value: 'Est. 2019', label: 'Incorporated 15 April 2019', icon: <DateRange fontSize="small" /> },
    { value: 'SIC 68320', label: 'Real estate management', icon: <Verified fontSize="small" /> },
    { value: 'TS1 3HU', label: '142 Victoria Road, Middlesbrough', icon: <Place fontSize="small" /> },
  ];

  // Demo form values – wire these to your listings search
  const [form, setForm] = useState({
    type: 'rent',
    location: '',
    min: '',
    max: '',
    beds: '',
  });

  const handleChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams({
      type: form.type,
      location: form.location,
      min: form.min,
      max: form.max,
      beds: form.beds,
    });
    window.location.href = `/properties?${params.toString()}`;
  };

  return (
    <Box
      component="section"
      aria-label="MRM Lettings Hero"
      sx={{
        position: 'relative',
        height: { xs: 'auto', md: '92vh' },
        minHeight: { xs: 620, md: 640 },
        width: '100%',
        overflow: 'hidden',
        color: 'common.white',
        backgroundColor: '#0b0b0b',
        // Mesh + gradient overlays
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(1200px 600px at 10% -10%, rgba(255,184,0,.40), transparent 40%), radial-gradient(1000px 600px at 90% 10%, rgba(255,77,77,.32), transparent 45%), linear-gradient(0deg, rgba(0,0,0,.75), rgba(0,0,0,.55))',
          zIndex: 1,
        },
        // Soft noise for luxury feel
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'140\' height=\'140\' viewBox=\'0 0 40 40\'%3E%3Cpath fill=\'%23ffffff0c\' d=\'M0 0h2v2H0zM8 18h1v1H8zM28 9h1v1h-1zM16 30h1v1h-1z\'/%3E%3C/svg%3E")',
          opacity: 0.6,
          mixBlendMode: 'overlay',
          pointerEvents: 'none',
          zIndex: 1,
        },
      }}
    >
      {/* Background video */}
      <Box
        component="video"
        src={heroVideoSrc}
        autoPlay
        muted
        loop
        playsInline
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
          filter: 'brightness(0.45)',
          background:
            'url(https://images.unsplash.com/photo-1501183638710-841dd1904471?q=80&w=2100&auto=format&fit=crop) center/cover',
        }}
      />

      {/* Curved bottom mask */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: -1,
          height: { xs: 70, sm: 90, md: 120 },
          background:
            'radial-gradient(100% 80% at 50% 0%, rgba(255,255,255,.18), transparent 70%)',
          zIndex: 1,
        }}
      />

      {/* Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1.1fr 0.9fr' },
          gap: { xs: 3, md: 6 },
          px: { xs: 3, md: 6, lg: 8 },
          py: { xs: 4, md: 6 },
          alignItems: { xs: 'flex-start', md: 'center' },
        }}
      >
        {/* Left: Copy + CTAs */}
        <Box>
          {/* Eyebrow */}
          <Typography
            variant="overline"
            sx={{
              ...createAnimationStyle('0.05s'),
              letterSpacing: 2.5,
              color: 'warning.main',
              fontWeight: 800,
            }}
          >
            Welcome to
          </Typography>

          {/* Heading */}
          <Typography
            variant="h2"
            component="h1"
            sx={{
              ...createAnimationStyle('0.20s'),
              fontWeight: 900,
              lineHeight: 1.05,
              maxWidth: 900,
            }}
          >
            MRM Lettings{' '}
            <Box component="span" sx={{ color: 'warning.light' }}>
              Property &amp; Management Ltd
            </Box>
          </Typography>

          {/* Tagline */}
          <Typography
            variant="h6"
            sx={{
              ...createAnimationStyle('0.32s'),
              mt: 1,
              color: 'grey.200',
              maxWidth: 760,
            }}
          >
            Local team, trusted experts, deep local knowledge
          </Typography>

          {/* Rating pill */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              ...createAnimationStyle('0.42s'),
              mt: 2,
              px: 1.25,
              py: 0.75,
              borderRadius: 999,
              bgcolor: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.4)',
              backdropFilter: 'blur(10px)',
              width: 'fit-content',
            }}
          >
            <Star sx={{ mr: 0.25 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              4.74 / 5
            </Typography>
            <Box sx={{ width: 4, height: 4, bgcolor: 'grey.300', borderRadius: '50%' }} />
            <Typography variant="body2" sx={{ color: 'grey.200' }}>
              1,571 Google Reviews
            </Typography>
          </Stack>

          {/* CTAs */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            flexWrap="wrap"
            sx={{ ...createAnimationStyle('0.55s'), mt: 3 }}
          >
            <Button
              variant="contained"
              color="warning"
              size="large"
              startIcon={<Assignment />}
              sx={{
                py: 1.5,
                px: 4,
                fontWeight: 800,
                color: 'grey.900',
                borderRadius: 2,
                transition: 'transform .25s ease',
                '&:hover': { transform: 'translateY(-2px)' },
                width: { xs: '100%', sm: 'auto' },
              }}
              href="/request-valuation"
            >
              Request a Valuation
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              startIcon={<HomeWork />}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                borderColor: 'rgba(255,255,255,.5)',
                color: 'common.white',
                '&:hover': { borderColor: 'common.white', backgroundColor: 'rgba(255,255,255,.08)' },
                width: { xs: '100%', sm: 'auto' },
              }}
              href="/properties"
            >
              Browse Properties
            </Button>
            <Button
              variant="text"
              color="inherit"
              size="large"
              startIcon={<Build />}
              sx={{ py: 1.5, px: 2, width: { xs: '100%', sm: 'auto' } }}
              href="/landlords"
            >
              For Landlords
            </Button>
            <Button
              variant="text"
              color="inherit"
              size="large"
              sx={{ py: 1.5, px: 2, width: { xs: '100%', sm: 'auto' } }}
              href="/tenants"
            >
              For Tenants
            </Button>
          </Stack>

          {/* Trust bar */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems="center"
            sx={{ ...createAnimationStyle('0.7s'), mt: 3 }}
          >
            <Chip
              icon={<Verified />}
              label="ARLA Registered"
              variant="outlined"
              sx={{ color: 'common.white', borderColor: 'rgba(255,255,255,.6)', bgcolor: 'rgba(0,0,0,0.32)' }}
            />
            <Chip
              icon={<Verified />}
              label="Client Money Protect"
              variant="outlined"
              sx={{ color: 'common.white', borderColor: 'rgba(255,255,255,.6)', bgcolor: 'rgba(0,0,0,0.32)' }}
            />
            <Chip
              icon={<Verified />}
              label="Rightmove & Zoopla Partner"
              variant="outlined"
              sx={{ color: 'common.white', borderColor: 'rgba(255,255,255,.6)', bgcolor: 'rgba(0,0,0,0.32)' }}
            />
          </Stack>
        </Box>

        {/* Right: Glass Search Card + Floating chips */}
        <Box sx={{ position: 'relative' }}>
          {/* Floating stat chips */}
          <Stack
            spacing={1}
            sx={{
              position: 'absolute',
              top: { xs: -10, md: -18 },
              right: { xs: 0, md: -8 },
              zIndex: 3,
            }}
          >
            <Chip
              icon={<MonetizationOn />}
              label="Average let time: 8 days"
              sx={{
                bgcolor: 'rgba(0,0,0,.38)',
                color: 'common.white',
                border: '1px solid rgba(255,255,255,.4)',
                backdropFilter: 'blur(4px)',
                animation: `${floatY} 5.5s ease-in-out infinite`,
              }}
            />
            <Chip
              icon={<Bed />}
              label="98% occupancy across our managed homes"
              sx={{
                bgcolor: 'rgba(0,0,0,.38)',
                color: 'common.white',
                border: '1px solid rgba(255,255,255,.4)',
                backdropFilter: 'blur(4px)',
                animation: `${floatY} 6.2s ease-in-out infinite`,
                animationDelay: '0.6s',
              }}
            />
          </Stack>

          {/* Search card */}
          <Paper
            elevation={0}
            sx={{
              ...createAnimationStyle('0.45s'),
              mt: { xs: 2, md: 0 },
              ml: { lg: 4 },
              px: { xs: 2.5, md: 3 },
              py: { xs: 2, md: 2.5 },
              borderRadius: 3,
              bgcolor: 'rgba(0,0,0,0.38)',
              border: '1px solid rgba(255,255,255,0.3)',
              backdropFilter: 'blur(14px)',
              maxWidth: 520,
            }}
          >
            <Typography variant="overline" sx={{ color: 'grey.100', letterSpacing: 1.6 }}>
              Quick property search
            </Typography>

            <Box component="form" onSubmit={handleSearch} sx={{ mt: 1.5 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    select
                    label="Type"
                    size="medium"
                    value={form.type}
                    onChange={handleChange('type')}
                    InputLabelProps={{ sx: { color: 'grey.200' } }}
                    sx={fieldSx}
                  >
                    <MenuItem value="rent">Rent</MenuItem>
                    <MenuItem value="sale">Sale</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Location"
                    placeholder="e.g., Middlesbrough, TS1"
                    value={form.location}
                    onChange={handleChange('location')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    InputLabelProps={{ sx: { color: 'grey.200' } }}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    label="Min £"
                    value={form.min}
                    onChange={handleChange('min')}
                    InputLabelProps={{ sx: { color: 'grey.200' } }}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    label="Max £"
                    value={form.max}
                    onChange={handleChange('max')}
                    InputLabelProps={{ sx: { color: 'grey.200' } }}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    select
                    label="Beds"
                    value={form.beds}
                    onChange={handleChange('beds')}
                    InputLabelProps={{ sx: { color: 'grey.200' } }}
                    sx={fieldSx}
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="1">1+</MenuItem>
                    <MenuItem value="2">2+</MenuItem>
                    <MenuItem value="3">3+</MenuItem>
                    <MenuItem value="4">4+</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    type="submit"
                    size="large"
                    variant="contained"
                    color="warning"
                    sx={{
                      mt: 0.5,
                      fontWeight: 800,
                      py: 1.4,
                      color: 'grey.900',
                      borderRadius: 2,
                    }}
                  >
                    Search Properties
                  </Button>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,.32)' }} />

              <Stack direction="row" spacing={1} flexWrap="wrap">
                {['Student lets', 'City centre', 'Pet friendly', 'Parking'].map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    clickable
                    sx={{
                      color: 'common.white',
                      borderColor: 'rgba(255,255,255,.4)',
                      bgcolor: 'rgba(0,0,0,.38)',
                      border: '1px solid rgba(255,255,255,.32)',
                    }}
                    onClick={() =>
                      setForm((s) => ({ ...s, location: tag }))
                    }
                  />
                ))}
              </Stack>
            </Box>
          </Paper>
        </Box>

        {/* Bottom: Company quick facts */}
        <Grid
          container
          spacing={2}
          sx={{
            gridColumn: '1 / -1',
            mt: { xs: 2, md: 'auto' },
            ...createAnimationStyle('0.8s'),
          }}
        >
          {stats.map((s) => (
            <Grid item xs={12} sm={4} key={s.label}>
              <Stack
                direction="row"
                spacing={1.2}
                alignItems="center"
                sx={{
                  px: 1.25,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: 'rgba(0,0,0,0.35)',
                  border: '1px solid rgba(255,255,255,0.28)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'rgba(0,0,0,.32)',
                  border: '1px solid rgba(255,255,255,.28)',
                }}
              >
                  {s.icon}
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ color: 'warning.light', fontWeight: 800, lineHeight: 1.1 }}>
                    {s.value}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'grey.300', textTransform: 'uppercase', letterSpacing: 1 }}>
                    {s.label}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Scroll cue */}
      <IconButton
        aria-label="Scroll for more"
        sx={{
          position: 'absolute',
          bottom: 18,
          left: '50%',
          transform: 'translateX(-50%)',
          color: 'white',
          animation: `${bounce} 2.2s infinite`,
          zIndex: 3,
        }}
        onClick={() => {
          const next = document.querySelector('[data-hero-next]');
          if (next) next.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      >
        <ArrowDownward />
      </IconButton>
    </Box>
  );
}

/* ====== Shared field styling for glass form ====== */
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: 'common.white',
    backdropFilter: 'blur(6px)',
    backgroundColor: 'rgba(0,0,0,0.32)',
    '& fieldset': { borderColor: 'rgba(255,255,255,.35)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,.55)' },
    '&.Mui-focused fieldset': { borderColor: 'warning.main', boxShadow: '0 0 0 1px rgba(255,184,0,0.5)' },
  },
  '& .MuiInputLabel-root': { color: 'grey.100' },
  '& .MuiSvgIcon-root': { color: 'grey.100' },
};
